import { Box, TextField, Typography } from '@mui/material';
import {
  type BaseSingleInputFieldProps,
  type DateValidationError,
  type FieldSection,
  TimePicker,
  type UseDateFieldProps,
} from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import useAppTheme from 'hooks/useAppTheme';
import { useState } from 'react';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { ThemePalette } from 'mui.theme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ButtonFieldForTimePickerProps
  extends UseDateFieldProps<Dayjs>,
    BaseSingleInputFieldProps<
      Dayjs | null,
      Dayjs,
      FieldSection,
      DateValidationError
    > {
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  error?: string;
}

export const CustomTextField = ({
  label,
  name,
  value,
  onChange,
  error,
  helperText,
  type = 'number',
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  helperText?: string;
  type?: string;
}) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        flexDirection: 'column',
        mb: '4px',
        width: '100%',
      }}
    >
      <TextField
        fullWidth
        label={label}
        name={name}
        type={type}
        value={value}
        onChange={(e) => {
          if (type === 'number') {
            const raw = e.target.value;
            if (raw !== '' && /^0\d/.test(raw)) {
              e.target.value = raw.replace(/^0+/, '');
            }
          }
          onChange(e as React.ChangeEvent<HTMLInputElement>);
        }}
        size="small"
        variant="outlined"
        error={!!error}
        onWheel={(e) => {
          (e.target as HTMLInputElement)?.blur();
        }}
        onKeyDown={(e) => {
          if (['e', 'E', '-', '+'].includes(e.key)) {
            e.preventDefault();
          }
        }}
        InputProps={{
          sx: {
            height: 36,
            fontSize: 14,
            padding: '0 8px',
            borderRadius: '8px',
          },
        }}
        InputLabelProps={{
          sx: {
            fontSize: '14px',
          },
        }}
        sx={{
          '& fieldset': {
            borderColor: enableDarkTheme ? '#616161' : '#E0E0E0',
            borderRadius: '8px',
          },
        }}
      />

      {error ? (
        <Typography
          color="error"
          sx={{
            fontSize: '10px',
            position: 'absolute',
            top: '100%',
            left: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

const ButtonFieldForTimePicker = (props: ButtonFieldForTimePickerProps) => {
  const {
    setOpen,
    label,
    id,
    disabled,
    error,
    InputProps: { ref } = {},
    inputProps: { 'aria-label': ariaLabel } = {},
  } = props;

  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  return (
    <CustomLoadingButton
      variant="outlined"
      id={id}
      disabled={disabled}
      ref={ref}
      aria-label={ariaLabel}
      onClick={() => setOpen?.((prev) => !prev)}
      // startIcon={<ClockIcon fontSize="small" />}
      sx={{
        borderRadius: '8px',
        fontSize: '14px',
        padding: '4px 8px',
        backgroundColor: enableDarkTheme
          ? palette.sectionBackground
          : '#f5f5f5',
        color: enableDarkTheme ? 'white' : ThemePalette.typography.black,
        fontWeight: 400,
        borderColor: enableDarkTheme ? '#616161' : '#E0E0E0',
      }}
      color={error ? 'error' : 'info'}
    >
      {typeof label === 'string' ? label : 'Pick a date'}
    </CustomLoadingButton>
  );
};

export const CustomTimePicker = ({
  value,
  onChange,
  error,
  min,
  max,
}: {
  value: string;
  onChange: (newValue: string | null) => void;
  error?: string;
  min?: string;
  max?: string;
}) => {
  const [open, setOpen] = useState(false);
  const { customerTz } = useCustomerInfo();

  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  if (!customerTz) return null;

  return (
    <Box>
      <TimePicker
        slots={{ field: ButtonFieldForTimePicker }}
        label={
          value == null ? null : dayjs.utc(value).tz(customerTz).format('HH:mm')
        }
        value={value ? dayjs.utc(value).tz(customerTz) : null}
        onChange={(newValue: Dayjs | null) => {
          onChange(newValue ? newValue.utc().toISOString() : null);
        }}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        onOpen={() => {
          setOpen(true);
        }}
        timeSteps={{
          minutes: 1,
        }}
        disableIgnoringDatePartForTimeValidation
        timezone={customerTz}
        format="HH:mm"
        ampm={false}
        minTime={min ? dayjs(min) : undefined}
        maxTime={max ? dayjs(max) : undefined}
        slotProps={{
          field: { setOpen, error } as any,
          textField: {
            size: 'small',
            sx: {
              '& fieldset': {
                borderColor: enableDarkTheme ? '#616161' : '#E0E0E0',
              },
            },
          },
          actionBar: { actions: [] },
        }}
      />
    </Box>
  );
};
